import { LocalDate } from "@js-joda/core";
import { InputField } from "components/designV2/inputfield/inputfield";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { PeriodNavigator } from "components/designV2/periodNavigator/periodNavigator";
import { MonthWorkingHours } from "model/Salary";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Api } from "services/api/api.service";
import { useAlertStore } from "stores/alertStore/alertStore";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { getMonthNameFromPeriod } from "web-apps/company/utils/utils";
import {
  ModuleSettingDescription,
  ModuleSettingText,
  ModuleSettingTitle,
  SystemSettingsActions,
} from "./companySystemSettings.styled";
import {
  MonthField,
  MonthLabel,
  WorkingHoursCard,
  WorkingHoursGrid,
  WorkingHoursHeader,
} from "./workingHoursSetting.styled";
import {
  parseWorkingHours,
  shouldRunWorkingHoursSave,
} from "./workingHoursSetting.logic";

type Props = {
  canSave: boolean;
  isSaving: boolean;
  onHasChangesChange: (hasChanges: boolean) => void;
  onIsSavingChange: (isSaving: boolean) => void;
  onSave: () => void;
  saveRequest: number;
  showSaveFeedback: boolean;
};

export const WorkingHoursSetting = ({
  canSave,
  isSaving,
  onHasChangesChange,
  onIsSavingChange,
  onSave,
  saveRequest,
  showSaveFeedback,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const [{ company }] = useCompanyStore();
  const [, , alertDispatch] = useAlertStore();
  const [year, setYear] = useState(new Date().getFullYear());
  const [months, setMonths] = useState<MonthWorkingHours[]>([]);
  const savedHoursRef = useRef(new Map<string, number>());
  const saveInFlightRef = useRef(false);
  const handledSaveRequestRef = useRef(0);
  const api = useMemo(() => Api(), []);
  const companyId = company?.id;

  useEffect(() => {
    if (!companyId) return;
    api.company.salary
      .getCompanyWorkHours(companyId, year)
      .then((response) => {
        savedHoursRef.current = new Map(
          response.data.map((month: MonthWorkingHours) => [
            month.period,
            month.hours,
          ]),
        );
        setMonths(response.data);
      })
      .catch(() => setMonths([]));
  }, [api, companyId, year]);

  const updateHours = (period: string, value: string): void => {
    const hours = parseWorkingHours(value);
    setMonths((previous) =>
      previous.map((month) =>
        month.period === period ? { ...month, hours } : month,
      ),
    );
  };

  const changedMonths = useMemo(
    () =>
      months.filter(
        (month) => savedHoursRef.current.get(month.period) !== month.hours,
      ),
    [months],
  );

  useEffect(() => {
    onHasChangesChange(changedMonths.length > 0);
  }, [changedMonths.length, onHasChangesChange]);

  useEffect(() => {
    if (
      !companyId ||
      !shouldRunWorkingHoursSave({
        changedMonthCount: changedMonths.length,
        companyId,
        handledSaveRequest: handledSaveRequestRef.current,
        isSaveInFlight: saveInFlightRef.current,
        saveRequest,
      })
    ) {
      return;
    }
    handledSaveRequestRef.current = saveRequest;
    saveInFlightRef.current = true;
    onIsSavingChange(true);

    Promise.allSettled(
      changedMonths.map((month) =>
        api.company.salary
          .overrideMonthWorkingHours(companyId, month.period, month.hours)
          .then(() => month),
      ),
    )
      .then((results) => {
        const successfulMonths = results.flatMap((result) =>
          result.status === "fulfilled" ? [result.value] : [],
        );
        successfulMonths.forEach((month) =>
          savedHoursRef.current.set(month.period, month.hours),
        );
        setMonths((previous) => [...previous]);

        if (showSaveFeedback) {
          alertDispatch({
            type: "SHOW_ALERT",
            payload:
              successfulMonths.length === changedMonths.length
                ? {
                    icon: "checkmark",
                    title: t("Salary.Changed"),
                    message: t("Salary.ChangedWorkingHours"),
                  }
                : {
                    icon: "alert",
                    title: t("Alerts.OhNo"),
                    message: t("Alerts.SomethingWentWrong"),
                  },
          });
        }
      })
      .finally(() => {
        saveInFlightRef.current = false;
        onIsSavingChange(false);
      });
  }, [
    alertDispatch,
    api,
    changedMonths,
    companyId,
    onIsSavingChange,
    saveRequest,
    showSaveFeedback,
    t,
  ]);

  const thisYear = new Date().getFullYear();
  return (
    <WorkingHoursCard>
      <WorkingHoursHeader>
        <ModuleSettingText>
          <ModuleSettingTitle>{t("Salary.MonthHours")}</ModuleSettingTitle>
          <ModuleSettingDescription>
            {t("Salary.WorkingHoursSettingsInfo")}
          </ModuleSettingDescription>
        </ModuleSettingText>
        <PeriodNavigator
          currentDate={LocalDate.of(year, 1, 1)}
          mode="year"
          onChange={(date) => setYear(date.year())}
          allowPrev={changedMonths.length === 0 && year > thisYear}
          allowNext={changedMonths.length === 0 && year < thisYear + 2}
        />
      </WorkingHoursHeader>
      <WorkingHoursGrid>
        {months.map((month) => (
          <MonthField key={month.period}>
            <MonthLabel>{getMonthNameFromPeriod(month.period)}</MonthLabel>
            <InputField
              type="number"
              value={String(month.hours)}
              onChange={(value) => updateHours(month.period, value)}
              placeholder="0"
              maxLength={3}
              size="compact"
            />
          </MonthField>
        ))}
      </WorkingHoursGrid>
      <SystemSettingsActions>
        <PrimaryButton
          label={t("General.Save")}
          size="large"
          disabled={!canSave}
          isLoading={isSaving}
          onClick={onSave}
        />
      </SystemSettingsActions>
    </WorkingHoursCard>
  );
};
